// src/UnderstandingPage.js
import React from 'react';

/*
 * "Understanding Your Report" – page 2, directly after the title page.
 * Keeps the 8.5 × 11‑inch canvas and RevCheck palette.
 */
export default function UnderstandingPage() {
  const colorBlue  = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#ececec';

  /* ------------------------------------------------------------------ */
  /*  Icon helpers – identical 24 × 24 px containers so text aligns      */
  /* ------------------------------------------------------------------ */
  const box = {
    display: 'inline-block', width: 24, height: 24, textAlign: 'center',
    verticalAlign: 'middle', lineHeight: '24px',
  };
  const IconLight = () => (
    <span style={box}>
      <img src="/MagnifyingGlass.png" alt="Inspect" style={{ width: 20, height: 20 }} />
    </span>
  );
  const IconModerate = () => (
    <span style={{ ...box, color: 'orange', fontSize: '1.35rem' }}>&#9888;</span>
  );
  const IconSevere = () => (
    <span style={{ ...box, color: 'red', fontSize: '1.6rem', fontWeight: 'bold' }}>&#10007;</span>
  );

  /* ------------------------------------------------------------------ */
  /*  Shared layout styles                                               */
  /* ------------------------------------------------------------------ */
  const PageStyle = {
    width: '8.5in', minHeight: '11in', margin: '0 auto', padding: '0.3in',
    backgroundColor: '#fff', border: '1px solid #ccc', boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif', position: 'relative',
  };
  const SectionHdr = {
    color: colorBlue, borderBottom: `1px solid ${colorBlue}`, paddingBottom: 5,
    marginBottom: 10, fontSize: '1.05rem',
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <div className="report-page" style={PageStyle}>
      {/* Title */}
      <h2 style={{ ...SectionHdr, fontSize: '1.25rem' }}>Understanding Your Report</h2>

      {/* Intro */}
      <p style={{ margin: '6px 0 14px' }}>
        We inspect every vehicle using a consistent process backed by industry standards so you can
        make an informed purchase with clarity and confidence.
      </p>

      {/* Severity Ratings */}
      <div style={{ marginBottom: '0.25in' }}>
        <h3 style={SectionHdr}>What the Severity Ratings Mean</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: lightGray2 }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', width: '22%' }}>Rating</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>What it Means</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: lightGray1 }}>
              <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                <IconLight /> Light
              </td>
              <td style={{ padding: '6px 8px' }}>
                Small flaws or normal wear for a used car—typically cosmetic. Examples include light
                scratches, small rock chips, minor curb rash, or mild interior wear.
              </td>
            </tr>
            <tr style={{ backgroundColor: lightGray2 }}>
              <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                <IconModerate /> Moderate
              </td>
              <td style={{ padding: '6px 8px' }}>
                More visible damage that may need attention soon and could affect appearance,
                comfort, or long‑term value. Examples: mismatched paint, uneven tire wear, faded
                headlights, interior rips.
              </td>
            </tr>
            <tr style={{ backgroundColor: lightGray1 }}>
              <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                <IconSevere /> Severe
              </td>
              <td style={{ padding: '6px 8px' }}>
                Issues that can affect safety, performance, or resale and may require immediate
                repair. Examples: broken lights, heavy rust, poorly‑done body repairs, bald tires.
              </td>
            </tr>
          </tbody>
        </table>
        <p style={{ margin: '8px 0 0', fontStyle: 'italic', fontSize: '0.9rem' }}>
          Structural, frame, air‑bag, fire, or flood damage is always rated <strong>Severe</strong>
          —even when minor—because safety isn’t negotiable.
        </p>
      </div>

      {/* Why scale matters */}
      <div style={{ marginBottom: '0.25in' }}>
        <h3 style={SectionHdr}>Why the Scale Matters</h3>
        <p style={{ margin: '6px 0' }}>
          Not all damage is equal—and not all buyers have the same priorities. Using National Auto
          Auction Association (NAAA) standards, precision tools (paint‑depth gauges, tread‑depth
          meters, OBD‑II scanners) and high‑resolution photos, our inspectors assign a clear rating
          to every finding so you know exactly what you’re getting.
        </p>
      </div>

      {/* What you’ll see */}
      <div style={{ marginBottom: '0.25in' }}>
        <h3 style={SectionHdr}>What You’ll See in This Report</h3>
        <ul style={{ margin: '6px 0 0 18px' }}>
          <li style={{ margin: '4px 0' }}>The part affected</li>
          <li style={{ margin: '4px 0' }}>A photo when a clear image can be captured</li>
          <li style={{ margin: '4px 0' }}>
            The severity level (
            <IconLight />{' '}/{' '}<IconModerate />{' '}/{' '}<IconSevere />)
          </li>
          <li style={{ margin: '4px 0' }}>A concise explanation when additional context helps</li>
        </ul>
        <p style={{ margin: '6px 0' }}>If an area isn’t listed, we found no concerns there.</p>
      </div>

      {/* History vs inspection */}
      <div style={{ marginBottom: '0.25in' }}>
        <h3 style={SectionHdr}>Why It’s Different from a History Report</h3>
        <p style={{ margin: '6px 0' }}>
          Carfax or AutoCheck tell you what was reported. We show you what’s <em>actually</em> present
          today. Hidden repairs, wear, or safety items that never make it to a database are revealed
          in a RevCheck inspection.
        </p>
      </div>

      {/* Footer */}
      <div style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.1in' }}>
        <hr style={{ border: 'none', borderTop: `1px solid ${colorBlue}` }} />
        <p style={{ margin: '4px 0' }}>Questions? We’re here to help—one inspection at a time.</p>
        <p style={{ margin: '4px 0' }}>
          Contact us at <a href="mailto:info@revcheckauto.com">info@revcheckauto.com</a> or (317)
          &nbsp;586‑2319.
        </p>
      </div>

      {/* Logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>
    </div>
  );
}
